import { useQuery } from '@apollo/client';
import { usePathname, useRouter } from 'next/navigation';
import { FC, useEffect, useRef, useState } from 'react';

import {
  Candidate,
  Company,
  ProfileMinQuery,
  ProfileMinQueryVariables
} from '@/__generated_graphql__/graphql';
import { SelectAdTypeModalDynamic as SelectAdTypeModal } from '@/components/SelectAdTypeModal/SelectAdTypeModalDynamic';
import { TABS } from '@/components/TapBar/TapBar.constants';
import { TapBarMenu } from '@/components/TapBar/TapBarMenu';
import { ROUTES } from '@/constants/common';
import { GET_PROFILE_MIN } from '@/graphql/queries';

import * as S from './TapBar.styles';

export const TapBar: FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isOpenedEntrySelect, setIsOpenedEntrySelect] = useState(false);
  const previousTab = useRef(0);

  const { push } = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    const matchedTab = TABS.findIndex(tab => tab.route === pathname);
    if (matchedTab !== -1) {
      setActiveTab(matchedTab);
    }
  }, [pathname]);

  const { data, loading } = useQuery<ProfileMinQuery, ProfileMinQueryVariables>(
    GET_PROFILE_MIN
  );

  if (loading) return null;

  const user = (data?.profile?.company ?? data?.profile?.candidate) as
    | (Company & Candidate)
    | null;

  const userName = user?.name ?? user?.name ?? user?.surname ?? '';
  const logoUrl = user && 'logoUrl' in user ? user.logoUrl : '';

  const handleCloseMenu = () => {
    if (isMenuOpen) {
      setIsMenuOpen(false);
    }
  };

  const handleOpenMenu = () => {
    setIsMenuOpen(true);
  };

  const tabActions = {
    [TABS[0].id]: () => {
      const isHomeTab = activeTab === TABS[0].id;
      const isHomePath = pathname === TABS[0].route;
      const isScrolledToTop = window.scrollY === 0;

      if (isHomeTab && !isScrolledToTop) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      if (!isHomePath) {
        push(TABS[0].route);
        return;
      }

      setActiveTab(TABS[0].id);
      handleCloseMenu();
      push(TABS[0].route);
    },
    [TABS[1].id]: () => {
      setActiveTab(TABS[1].id);
      handleCloseMenu();
      push(TABS[1].route);
    },
    [TABS[2].id]: () => {
      setIsOpenedEntrySelect(true);
    },
    [TABS[3].id]: () => {
      if (!user?.id) {
        push(ROUTES.LOGIN);
        return;
      }
      setActiveTab(TABS[3].id);
      handleCloseMenu();
      push(TABS[3].route);
    },
    [TABS[4].id]: () => {
      if (activeTab === TABS[4].id) {
        setActiveTab(previousTab.current);
        if (isMenuOpen) {
          handleCloseMenu();
        } else {
          // Defensive: re-open menu if somehow closed while tab 4 is active.
          // Not reachable in tests — tab 4 always opens the menu on first click.
          /* istanbul ignore next */
          handleOpenMenu();
        }
        return;
      }
      previousTab.current = activeTab;
      setActiveTab(TABS[4].id);
      handleOpenMenu();
    }
  };

  return (
    <S.Wrapper>
      <SelectAdTypeModal
        isOpen={isOpenedEntrySelect}
        onClose={() => setIsOpenedEntrySelect(false)}
      />
      <TapBarMenu isOpen={isMenuOpen} />

      <S.BarWrapper>
        <S.TopIndicator $activeTab={activeTab} />
        {TABS.map((tab, index) => (
          <S.TabItem
            key={tab?.id}
            $isActive={activeTab === index}
            onClick={tabActions[index]}
            role="button"
            data-testid="tapbar-menu-button"
            data-cy={tab['data-cy']}
          >
            {tab.renderContent
              ? tab.renderContent(userName, logoUrl)
              : tab.icon}
          </S.TabItem>
        ))}
      </S.BarWrapper>
    </S.Wrapper>
  );
};
